import React, { useMemo, useState } from "react";
import {
    Box,
    Chip,
    CircularProgress,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import GetData from "../../services/GetData.js";
import SnackbarEx from "../Helpers/SnackbarEx.jsx";
import AuthenticatedAssetImage from "../Helpers/AuthenticatedAssetImage.jsx";
import { catalogSearchFieldSx } from "./catalogGridStyles.js";
import "../InventoryUpload/uploadProducts.css";
import "./buyerView.css";

const BIDDING_THRESHOLD = 0.5;

const PRICE_COLUMNS = [
    { key: "vistaMaxPrice", label: "Vista Max", auction: false },
    { key: "macMaxPrice", label: "MAC Max", auction: true },
    { key: "bidftaMaxPrice", label: "BidFTA Max", auction: true },
    { key: "triangleAndersonPrice", label: "Triangle Anderson", auction: true },
    { key: "triangleRaleighPrice", label: "Triangle Raleigh", auction: true },
];

function moneyText(value) {
    if (value == null || value === "") return "";
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function toPrice(value) {
    if (value == null || value === "") return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function numOrBlank(value) {
    if (value == null || value === "") return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function numOrZero(value) {
    return numOrBlank(value) ?? 0;
}

/** Match server productFormulas.roundHalfUp / roundDown. */
function roundHalfUp(value, digits) {
    const n = numOrBlank(value);
    if (n == null) return null;
    const factor = 10 ** digits;
    return Math.round(n * factor) / factor;
}

function roundDown(value, digits) {
    const n = numOrBlank(value);
    if (n == null) return null;
    const factor = 10 ** digits;
    return Math.trunc(n * factor) / factor;
}

/** Live Buyer View calculator — not persisted. */
function computeVistaPackPrice({
    partialPackEligible,
    maxPurchase,
    innerPacks,
    viablePackCount,
}) {
    if (partialPackEligible !== "Yes") return 0;
    const inner = numOrBlank(innerPacks);
    if (!inner) return 0;
    const raw = roundHalfUp(
        ((numOrZero(maxPurchase) / inner) * numOrZero(viablePackCount) - 2) / 1.15,
        2
    );
    if (raw == null || raw < 1) return 0;
    return raw;
}

function computeMacPackPrice({
    partialPackEligible,
    maxPurchase,
    innerPacks,
    viablePackCount,
}) {
    if (partialPackEligible !== "Yes") return 0;
    const inner = numOrBlank(innerPacks);
    if (!inner) return 0;
    const raw = roundDown(
        ((numOrZero(maxPurchase) / inner) * numOrZero(viablePackCount) - 3) / 1.15,
        0
    );
    if (raw == null || raw < 1) return 0;
    return raw;
}

function hasPriceAboveThreshold(product) {
    return PRICE_COLUMNS.some(({ key }) => {
        const n = toPrice(product?.[key]);
        return n != null && n > BIDDING_THRESHOLD;
    });
}

function statusNames(value) {
    if (!Array.isArray(value)) return [];
    return value
        .map((entry) => (entry && typeof entry === "object" ? entry.name || "" : ""))
        .filter(Boolean);
}

function heroAssetFromProduct(product) {
    const photos = Array.isArray(product?.heroPhoto) ? product.heroPhoto : [];
    return photos.find((asset) => asset?.gcsObject || asset?.sourceUrl) || null;
}

function shapeProduct(product) {
    return {
        id: product._id,
        heroAsset: heroAssetFromProduct(product),
        sku: product.listingField || product.internalSkuId || "",
        itemName: product.itemName || product.listingField || "",
        vistaMaxPrice: product.vistaMaxPrice,
        macMaxPrice: product.macMaxPrice,
        bidftaMaxPrice: product.bidftaMaxPrice,
        triangleAndersonPrice: product.triangleAndersonPrice,
        triangleRaleighPrice: product.triangleRaleighPrice,
        partialPackEligible: product.partialPackEligible || "",
        innerPacks: product.innerPacks,
        maxPurchasePrice: product.maxPurchasePrice,
        statusChecks: statusNames(product.statusChecks),
    };
}

function PriceColumn({ label, value, auction }) {
    const n = toPrice(value);
    const belowThreshold = auction && n != null && n <= BIDDING_THRESHOLD;
    const text = moneyText(value);

    return (
        <div className="buyer-price-col">
            <Typography className="buyer-price-col-label" component="div">
                {label}
            </Typography>
            {belowThreshold ? (
                <>
                    {text ? (
                        <Typography className="buyer-price-col-value is-muted" component="div">
                            {text}
                        </Typography>
                    ) : null}
                    <Typography className="buyer-price-col-threshold" component="div">
                        Below $0.5 bidding threshold
                    </Typography>
                </>
            ) : text ? (
                <Typography className="buyer-price-col-value" component="div">
                    {text}
                </Typography>
            ) : (
                <Typography className="buyer-price-col-value is-empty" component="div">
                    —
                </Typography>
            )}
        </div>
    );
}

function PartialPackSection({ product, viablePackCount, onViablePackCountChange }) {
    const eligible = product.partialPackEligible || "—";
    const packPrices = useMemo(
        () => ({
            vista: computeVistaPackPrice({
                partialPackEligible: product.partialPackEligible,
                maxPurchase: product.maxPurchasePrice,
                innerPacks: product.innerPacks,
                viablePackCount,
            }),
            mac: computeMacPackPrice({
                partialPackEligible: product.partialPackEligible,
                maxPurchase: product.maxPurchasePrice,
                innerPacks: product.innerPacks,
                viablePackCount,
            }),
        }),
        [
            product.partialPackEligible,
            product.maxPurchasePrice,
            product.innerPacks,
            viablePackCount,
        ]
    );

    return (
        <div className="buyer-partial-pack">
            <Typography className="buyer-partial-pack-title" component="h3">
                Partial Pack Calculator
            </Typography>
            <div className="buyer-partial-pack-fields">
                <div className="buyer-partial-pack-field">
                    <Typography className="buyer-partial-pack-label" component="div">
                        Partial Pack Eligible
                    </Typography>
                    <Typography
                        className={`buyer-partial-pack-value${
                            eligible === "Yes" ? " is-yes" : eligible === "No" ? " is-no" : ""
                        }`}
                        component="div"
                    >
                        {eligible}
                    </Typography>
                </div>
                <div className="buyer-partial-pack-field">
                    <Typography
                        className="buyer-partial-pack-label"
                        component="label"
                        htmlFor={`viable-packs-${product.id}`}
                    >
                        # of Viable Packs
                    </Typography>
                    <input
                        id={`viable-packs-${product.id}`}
                        className="buyer-viable-input"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoComplete="off"
                        value={viablePackCount}
                        placeholder="—"
                        disabled={eligible !== "Yes"}
                        onChange={(e) => {
                            const next = e.target.value.replace(/[^\d]/g, "");
                            onViablePackCountChange(next);
                        }}
                    />
                </div>
            </div>
            <div className="buyer-partial-pack-prices">
                <div className="buyer-partial-pack-price">
                    <Typography className="buyer-partial-pack-label" component="div">
                        Vista Pack Price
                    </Typography>
                    <Typography className="buyer-partial-pack-price-value" component="div">
                        {moneyText(packPrices.vista) || "$0.00"}
                    </Typography>
                </div>
                <div className="buyer-partial-pack-price">
                    <Typography className="buyer-partial-pack-label" component="div">
                        Mac Pack Price
                    </Typography>
                    <Typography className="buyer-partial-pack-price-value" component="div">
                        {moneyText(packPrices.mac) || "$0.00"}
                    </Typography>
                </div>
            </div>
            <div className="buyer-partial-pack-max">
                <Typography className="buyer-partial-pack-label" component="div">
                    Max Purchase Price
                </Typography>
                <Chip
                    size="small"
                    label={moneyText(product.maxPurchasePrice) || "—"}
                    className="buyer-max-purchase-chip"
                />
            </div>
        </div>
    );
}

function BuyerResultRow({ product, viablePackCount, onViablePackCountChange }) {
    return (
        <section className="buyer-result-row">
            <div className="buyer-result-hero">
                <AuthenticatedAssetImage
                    asset={product.heroAsset}
                    alt={product.itemName || product.sku || ""}
                    sx={{
                        width: "100%",
                        maxWidth: 220,
                        aspectRatio: "1 / 1",
                        borderRadius: "12px",
                        border: "1px solid #e2e8f0",
                        backgroundColor: "#f8fafc",
                    }}
                />
                <Typography className="buyer-result-sku" component="div">
                    {product.sku || "—"}
                </Typography>
                <Typography className="buyer-result-name" component="h2">
                    {product.itemName || "Untitled product"}
                </Typography>
                {product.statusChecks?.length > 0 ? (
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                            justifyContent: "center",
                            mt: 1,
                        }}
                    >
                        {product.statusChecks.map((name) => (
                            <Tooltip key={name} title={name} enterDelay={300}>
                                <Chip
                                    size="small"
                                    label={name}
                                    sx={{
                                        height: 24,
                                        maxWidth: 180,
                                        fontWeight: 600,
                                        fontSize: 12,
                                        borderRadius: "8px",
                                        backgroundColor: "#e0f2fe",
                                        color: "#075985",
                                    }}
                                />
                            </Tooltip>
                        ))}
                    </Box>
                ) : null}
            </div>
            <div className="buyer-result-pricing">
                <div className="buyer-price-columns">
                    {PRICE_COLUMNS.map((col) => (
                        <PriceColumn
                            key={col.key}
                            label={col.label}
                            value={product[col.key]}
                            auction={col.auction}
                        />
                    ))}
                </div>
                <PartialPackSection
                    product={product}
                    viablePackCount={viablePackCount}
                    onViablePackCountChange={onViablePackCountChange}
                />
            </div>
        </section>
    );
}

export default function BuyerViewSearch() {
    const [searchSku, setSearchSku] = useState("");
    const [loading, setLoading] = useState(false);
    const [matches, setMatches] = useState([]);
    /** Session-only viable pack inputs keyed by product id — never persisted. */
    const [viableByProductId, setViableByProductId] = useState({});
    const [searched, setSearched] = useState(false);
    const [snack, setSnack] = useState({ color: "", message: "", open: false });

    const resetResult = () => {
        setMatches([]);
        setViableByProductId({});
        setSearched(false);
    };

    const submitSearch = async (event) => {
        event.preventDefault();
        const sku = String(searchSku || "").trim();
        if (!sku) {
            setSnack({ color: "warning", message: "Enter a search term.", open: true });
            return;
        }

        setLoading(true);
        setSearched(true);
        const params = new URLSearchParams({
            page: "1",
            pageSize: "50",
            sku,
            includeAssets: "1",
        });
        const { getData, error } = await GetData(
            `broylesllc/products?${params.toString()}`
        );
        setLoading(false);

        if (error) {
            setMatches([]);
            setViableByProductId({});
            setSnack({
                color: "error",
                message: "Failed to search products.",
                open: true,
            });
            return;
        }

        const rawItems = getData?.items || [];
        const items = rawItems.filter(hasPriceAboveThreshold).map(shapeProduct);

        if (!items.length) {
            setMatches([]);
            setViableByProductId({});
            setSnack({
                color: "error",
                message: rawItems.length
                    ? "Found matches, but none have a price above $0.5."
                    : "No products matched that search.",
                open: true,
            });
            return;
        }

        const exact = items.find(
            (item) =>
                String(item.sku || "").toLowerCase() === sku.toLowerCase()
        );
        // Exact SKU first, then the rest — all shown as rows.
        const ordered = exact
            ? [exact, ...items.filter((item) => item.id !== exact.id)]
            : items;
        setMatches(ordered);
        setViableByProductId({});
    };

    if (matches.length) {
        return (
            <>
                <div className="buyer-results">
                    <div className="buyer-results-toolbar">
                        <Typography className="buyer-results-count" component="div">
                            {matches.length} match{matches.length === 1 ? "" : "es"}
                        </Typography>
                        <button
                            type="button"
                            className="buyer-result-clear"
                            onClick={() => {
                                resetResult();
                                setSearchSku("");
                            }}
                        >
                            New search
                        </button>
                    </div>
                    <div className="buyer-results-list">
                        {matches.map((product) => (
                            <BuyerResultRow
                                key={product.id}
                                product={product}
                                viablePackCount={viableByProductId[product.id] ?? ""}
                                onViablePackCountChange={(value) => {
                                    setViableByProductId((prev) => ({
                                        ...prev,
                                        [product.id]: value,
                                    }));
                                }}
                            />
                        ))}
                    </div>
                </div>
                {snack.open && (
                    <SnackbarEx
                        color={snack.color}
                        message={snack.message}
                        open={snack.open}
                        close={() => setSnack({ color: "", message: "", open: false })}
                    />
                )}
            </>
        );
    }

    return (
        <>
            <form onSubmit={submitSearch} className="upload-search-hero">
                <span className="upload-channel-pill buyer-view-pill">Buyer View</span>
                <label className="upload-search-hero-label" htmlFor="buyer-sku-search">
                    Search products
                </label>
                <TextField
                    id="buyer-sku-search"
                    size="small"
                    disabled={loading}
                    name="sku"
                    value={searchSku}
                    placeholder="SKU, listing field, brand, or item name"
                    onChange={(e) => setSearchSku(e.target.value)}
                    sx={{ ...catalogSearchFieldSx, width: "100%" }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchOutlinedIcon sx={{ color: "#9aa3ad", fontSize: 18 }} />
                            </InputAdornment>
                        ),
                        endAdornment: loading ? (
                            <InputAdornment position="end">
                                <CircularProgress size={18} sx={{ color: "#0d9488" }} />
                            </InputAdornment>
                        ) : null,
                    }}
                />
                <Typography className="upload-search-hero-hint" component="p">
                    {searched
                        ? "No priced match yet — try another search."
                        : "Matches SKU, listing field, brand, or name. Shows results with a price above $0.5."}
                </Typography>
            </form>

            {snack.open && (
                <SnackbarEx
                    color={snack.color}
                    message={snack.message}
                    open={snack.open}
                    close={() => setSnack({ color: "", message: "", open: false })}
                />
            )}
        </>
    );
}
